import { useEffect, useRef, useState } from 'react'

import {
    browserName,
    devicePlatform,
    isStandalone,
} from 'modules/webPush'
import { runWebPushSelfTest, SelfTestFailure } from 'modules/webPush/selfTest'
import type { WebPushShownMessage } from 'swPush'
import { queueEvent } from 'utilities/analytics'
import {
    blockedSteps,
    deliverySteps,
    deviceSteps,
    settingsAppName,
} from './guidance'
import Steps from './steps'

type Hidden = 'os' | 'not_seen' | 'blocked'

type Phase =
    | { step: 'idle' }
    | { step: 'running' }
    // Shown and listed: only the person can say whether it was on screen.
    | { step: 'ask' }
    | { step: 'ok' }
    | { step: 'hidden'; why: Hidden }
    | { step: 'not_received' }
    | { step: 'failed'; reason: SelfTestFailure }

const FAILURES: Record<SelfTestFailure, string> = {
    not_subscribed:
        'این دستگاه برای اعلان ثبت نشد. کلید «اعلان روی این دستگاه» را خاموش و دوباره روشن کنید.',
    too_soon: 'چند ثانیه صبر کنید و دوباره بزنید.',
    capped: 'امروز به سقف اعلان‌های آزمایشی رسیدید. فردا دوباره امتحان کنید.',
    unavailable: 'سرویس اعلان همیان الان در دسترس نیست. کمی بعد امتحان کنید.',
    offline:
        'درخواست فرستاده نشد. اینترنت را بررسی کنید و دوباره امتحان کنید.',
}

const HIDDEN_TITLES: Record<Hidden, string> = {
    os: 'اعلان به مرورگر رسید، اما دستگاه نشانش نداد',
    not_seen: 'اعلان به مرورگر رسید؛ پس دستگاه جلوی نمایشش را گرفته',
    blocked: 'مرورگر اجازهٔ نمایش اعلان را نداد',
}

/** The phase the service worker's report leads to. */
const phaseOf = (report: Pick<WebPushShownMessage, 'listed' | 'error'>): Phase => {
    if (report.error) return { step: 'hidden', why: 'blocked' }
    // Shown, yet not among the browser's notifications: the OS dropped it.
    if (report.listed === false) return { step: 'hidden', why: 'os' }
    return { step: 'ask' }
}

const resultOf = (phase: Phase): string => {
    if (phase.step !== 'hidden') return 'shown'
    return phase.why === 'os' ? 'os_hidden' : 'show_failed'
}

/**
 * «اعلان نمی‌رسد؟»: push a test to this browser and say where it stopped.
 *
 * From the server every push looks delivered, so this is the only place that
 * can tell «never reached the browser» from «the device hid it» — and point
 * at the setting to change.
 */
export default function WebPushSelfTest() {
    const [phase, setPhase] = useState<Phase>({ step: 'idle' })
    const attempt = useRef(0)
    const mounted = useRef(true)

    const platform = devicePlatform()
    const browser = browserName()
    const app = settingsAppName(platform, browser)
    const context = { platform, browser, standalone: isStandalone() }

    useEffect(
        () => () => {
            mounted.current = false
        },
        []
    )

    const start = async () => {
        if (phase.step === 'running') return
        const id = ++attempt.current
        const current = () => mounted.current && attempt.current === id
        setPhase({ step: 'running' })
        queueEvent('web_push_test_requested', { ...context, attempt: id })

        const outcome = await runWebPushSelfTest()
        if (!current()) return

        if (outcome.kind === 'send_failed') {
            setPhase({ step: 'failed', reason: outcome.reason })
            queueEvent('web_push_test_result', {
                ...context,
                result: 'send_failed',
                reason: outcome.reason,
                repaired: outcome.repaired,
            })
            return
        }

        if (outcome.kind === 'not_received') {
            setPhase({ step: 'not_received' })
            queueEvent('web_push_test_result', {
                ...context,
                result: 'not_received',
                ms: outcome.ms,
            })
            outcome.late.then((report) => {
                if (!report) return
                const next = phaseOf(report)
                queueEvent('web_push_test_late', {
                    ...context,
                    result: resultOf(next),
                })
                if (current()) setPhase(next)
            })
            return
        }

        const report =
            outcome.kind === 'shown'
                ? { listed: outcome.listed, error: null }
                : { listed: null, error: outcome.error }
        const next = phaseOf(report)
        setPhase(next)
        queueEvent('web_push_test_result', {
            ...context,
            result: resultOf(next),
            listed: report.listed,
            error: report.error,
            ms: outcome.ms,
        })
    }

    const answer = (seen: boolean) => {
        setPhase(seen ? { step: 'ok' } : { step: 'hidden', why: 'not_seen' })
        queueEvent('web_push_test_answered', { ...context, seen })
    }

    const running = phase.step === 'running'

    return (
        <section className="bg-white rounded-lg shadow-header p-4">
            <p className="text-base font-bold">اعلان نمی‌رسد؟</p>
            <p className="text-xs text-text-secondary mt-1 leading-6">
                یک اعلان آزمایشی به همین دستگاه بفرستید تا معلوم شود مشکل کجاست.
            </p>
            <button
                type="button"
                onClick={start}
                disabled={running}
                className="mt-3 w-full bg-green-1 text-white text-sm rounded-md py-2 px-4 disabled:opacity-60"
            >
                {running ? 'منتظر رسیدن اعلان…' : 'ارسال اعلان آزمایشی'}
            </button>

            <div aria-live="polite">
                {phase.step === 'ask' && (
                    <div className="mt-3 rounded-md bg-canvas p-3">
                        <p className="text-sm font-bold">
                            «اعلان آزمایشی همیان» را روی صفحه دیدید؟
                        </p>
                        <p className="text-xs text-text-secondary mt-1 leading-6">
                            مرورگر آن را نمایش داد. اگر روی صفحه نیامد، مرکز
                            اعلان‌های دستگاه را هم نگاه کنید.
                        </p>
                        <div className="flex gap-2 mt-3">
                            <button
                                type="button"
                                onClick={() => answer(true)}
                                className="bg-green-1 text-white text-sm rounded-md py-2 px-4 grow"
                            >
                                بله، دیدم
                            </button>
                            <button
                                type="button"
                                onClick={() => answer(false)}
                                className="text-text-secondary text-sm rounded-md py-2 px-4 border border-divider grow"
                            >
                                نه، نیامد
                            </button>
                        </div>
                    </div>
                )}

                {phase.step === 'ok' && (
                    <p className="mt-3 text-xs leading-6 rounded-md bg-success-2 text-success-on p-3">
                        اعلان‌های همیان روی این دستگاه درست کار می‌کند.
                    </p>
                )}

                {phase.step === 'hidden' && (
                    <div className="mt-3 rounded-md bg-warning-soft text-warning-on p-3">
                        <p className="text-sm font-bold">
                            {HIDDEN_TITLES[phase.why]}
                        </p>
                        <p className="text-xs mt-1 leading-6">
                            ارسال از طرف همیان انجام شده؛ این تنظیمات دستگاه را
                            بررسی کنید:
                        </p>
                        <Steps
                            steps={
                                phase.why === 'blocked'
                                    ? blockedSteps(platform, app)
                                    : deviceSteps(platform, app)
                            }
                        />
                        <p className="text-xs mt-2 leading-6">
                            بعد دوباره «ارسال اعلان آزمایشی» را بزنید.
                        </p>
                    </div>
                )}

                {phase.step === 'not_received' && (
                    <div className="mt-3 rounded-md bg-warning-soft text-warning-on p-3">
                        <p className="text-sm font-bold">
                            اعلان تا ۳۰ ثانیه به این مرورگر نرسید
                        </p>
                        <Steps steps={deliverySteps(platform, app)} />
                        <p className="text-xs mt-2 leading-6">
                            اگر دیرتر برسد، نتیجه همین‌جا به‌روز می‌شود.
                        </p>
                    </div>
                )}

                {phase.step === 'failed' && (
                    <p className="mt-3 text-xs leading-6 rounded-md bg-danger-soft text-danger-on p-3">
                        {FAILURES[phase.reason]}
                    </p>
                )}
            </div>
        </section>
    )
}
